<template>
  <b-modal
    id="sli-kml-map-modal"
    v-model="open"
    size="lg"
    centered
    scrollable
    :title="title || 'عرض ملف KML على الخريطة'"
    ok-only
    ok-title="إغلاق"
    modal-class="sli-kml-modal"
    @shown="renderMap"
    @hidden="destroyMap"
  >
    <p
      v-if="error"
      class="sli-kml-error"
    >
      {{ error }}
    </p>
    <p
      v-else-if="loading"
      class="text-center mb-1"
    >
      جاري تجهيز الخريطة...
    </p>
    <div
      ref="mapEl"
      class="sli-kml-map"
    />
  </b-modal>
</template>

<script>
import { BModal } from 'bootstrap-vue'
import { loadLeafletAssets } from '@/utils/leafletLoader'
import { BRAND_NAVY, BRAND_LIME } from '@/libs/brandColors'
import { coordinatesToLatLngs } from '../formModel'

function isKmzName(name) {
  return String(name || '').toLowerCase().endsWith('.kmz')
}

function parseCoordText(text) {
  return String(text || '').trim().split(/\s+/).map(pair => {
    const parts = pair.split(',')
    const lng = parseFloat(parts[0])
    const lat = parseFloat(parts[1])
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng]
    return null
  }).filter(Boolean)
}

function firstCoordinates(node) {
  const list = node.getElementsByTagName('coordinates')
  return list.length ? parseCoordText(list[0].textContent) : []
}

function parseKmlShapes(text) {
  const doc = new DOMParser().parseFromString(text, 'text/xml')
  if (doc.querySelector('parsererror')) {
    throw new Error('INVALID_KML')
  }
  const shapes = []
  Array.from(doc.getElementsByTagName('Polygon')).forEach(node => {
    const latlngs = firstCoordinates(node)
    if (latlngs.length) shapes.push({ type: 'polygon', latlngs })
  })
  Array.from(doc.getElementsByTagName('LineString')).forEach(node => {
    const latlngs = firstCoordinates(node)
    if (latlngs.length) shapes.push({ type: 'line', latlngs })
  })
  Array.from(doc.getElementsByTagName('Point')).forEach(node => {
    const latlngs = firstCoordinates(node)
    if (latlngs[0]) shapes.push({ type: 'point', latlngs: latlngs[0] })
  })
  if (!shapes.length) {
    Array.from(doc.getElementsByTagName('coordinates')).forEach(node => {
      const latlngs = parseCoordText(node.textContent)
      if (latlngs.length === 1) shapes.push({ type: 'point', latlngs: latlngs[0] })
      else if (latlngs.length > 1) shapes.push({ type: 'line', latlngs })
    })
  }
  if (!shapes.length) throw new Error('EMPTY_KML')
  return shapes
}

export default {
  name: 'SliKmlMapModal',
  components: { BModal },
  props: {
    visible: { type: Boolean, default: false },
    file: { default: null },
    url: { type: String, default: '' },
    title: { type: String, default: '' },
    coordinates: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      error: '',
      map: null,
      layer: null,
    }
  },
  computed: {
    open: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      },
    },
  },
  methods: {
    readFileText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = event => resolve(event.target.result)
        reader.onerror = reject
        reader.readAsText(file)
      })
    },
    async readKmlText() {
      if (this.file instanceof File) {
        if (isKmzName(this.file.name)) {
          throw new Error('KMZ_UNSUPPORTED')
        }
        return this.readFileText(this.file)
      }
      if (this.url) {
        if (isKmzName(this.url)) {
          throw new Error('KMZ_UNSUPPORTED')
        }
        const response = await fetch(this.url)
        if (!response.ok) throw new Error('FETCH_FAILED')
        return response.text()
      }
      throw new Error('NO_SOURCE')
    },
    shapesFromCoordinates() {
      const latlngs = coordinatesToLatLngs(this.coordinates)
      return latlngs.length >= 3 ? [{ type: 'polygon', latlngs }] : []
    },
    async renderMap() {
      this.destroyMap()
      this.error = ''
      this.loading = true
      try {
        const L = await loadLeafletAssets()
        let shapes = this.shapesFromCoordinates()
        if (!shapes.length) {
          shapes = parseKmlShapes(await this.readKmlText())
        }
        await this.$nextTick()
        if (!this.$refs.mapEl) return
        this.map = L.map(this.$refs.mapEl, { zoomControl: true })
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap',
        }).addTo(this.map)
        this.layer = L.featureGroup().addTo(this.map)
        const pathStyle = {
          color: BRAND_NAVY,
          weight: 2,
          fillColor: BRAND_LIME,
          fillOpacity: 0.28,
        }
        shapes.forEach(shape => {
          if (shape.type === 'polygon') {
            L.polygon(shape.latlngs, pathStyle).addTo(this.layer)
          } else if (shape.type === 'line') {
            L.polyline(shape.latlngs, pathStyle).addTo(this.layer)
          } else if (shape.type === 'point') {
            L.circleMarker(shape.latlngs, {
              radius: 6,
              color: BRAND_NAVY,
              weight: 2,
              fillColor: BRAND_LIME,
              fillOpacity: 0.9,
            }).addTo(this.layer)
          }
        })
        const bounds = this.layer.getBounds()
        if (bounds.isValid()) {
          this.map.fitBounds(bounds.pad(0.18))
        } else {
          this.map.setView([26.82, 30.8], 6)
        }
        this.$nextTick(() => {
          if (this.map) this.map.invalidateSize()
        })
      } catch (err) {
        if (err && err.message === 'KMZ_UNSUPPORTED') {
          this.error = 'عرض الخريطة متاح لملفات KML. ملف KMZ يُحفظ مع القطعة لكن لا يُعرض هنا.'
        } else if (err && err.message === 'EMPTY_KML') {
          this.error = 'لم يتم العثور على إحداثيات داخل ملف KML.'
        } else {
          this.error = 'تعذر عرض ملف KML على الخريطة.'
        }
      } finally {
        this.loading = false
      }
    },
    destroyMap() {
      if (this.map) {
        this.map.remove()
        this.map = null
        this.layer = null
      }
    },
  },
}
</script>

<style>
.sli-kml-map {
  height: 420px;
  width: 100%;
  border-radius: 6px;
  background: #e8eef5;
}
.sli-kml-error {
  color: #b42318;
  font-weight: 600;
  margin-bottom: 8px;
}
@media (max-width: 1199.98px) {
  .sli-kml-map {
    height: min(52vh, 400px);
  }
}
@media (max-width: 767.98px) {
  .sli-kml-map {
    height: min(48vh, 320px);
  }
}
</style>
